/**
 * Everything that persists between visits.
 *
 * There is no backend and no account. Three things live in localStorage:
 *   1. Settings (API key, gauge, wrist) — hers, on her device.
 *   2. Knot-along progress — so closing the tab mid-bracelet does not lose her
 *      place, which is the single most important reliability property of the
 *      app for someone knotting over several evenings.
 *   3. A short list of recently opened designs.
 *
 * Every read is defensive. localStorage can be full, disabled, or contain
 * something from an older version, and none of those should stop her opening
 * the app.
 */
import { DEFAULT_GAUGE, type Gauge } from '../engine/thread';
import { DEFAULT_SIZING } from '../brief/types';
import type { Sizing } from '../engine/types';

const KEY_SETTINGS = 'knotwright.settings.v1';
const KEY_PROGRESS = 'knotwright.progress.v1';
const KEY_RECENT = 'knotwright.recent.v1';

export interface Settings {
  /** The user's own Gemini key. Never sent anywhere but Google. */
  geminiKey: string;
  geminiModel: string;
  gauge: Gauge;
  sizing: Sizing;
  /** Show lengths in inches as well as cm. */
  showInches: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  geminiKey: '',
  geminiModel: 'gemini-2.0-flash',
  gauge: DEFAULT_GAUGE,
  sizing: DEFAULT_SIZING,
  showInches: false,
};

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Storage full or blocked. Nothing to do but carry on without it. */
  }
}

export function loadSettings(): Settings {
  const raw = safeGet(KEY_SETTINGS);
  if (!raw) return DEFAULT_SETTINGS;
  try {
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      gauge: { ...DEFAULT_GAUGE, ...(parsed.gauge ?? {}) },
      sizing: { ...DEFAULT_SIZING, ...(parsed.sizing ?? {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(s: Settings): void {
  safeSet(KEY_SETTINGS, JSON.stringify(s));
}

/** Wipe the key, for the "forget my key" button. */
export function clearGeminiKey(): Settings {
  const s = { ...loadSettings(), geminiKey: '' };
  saveSettings(s);
  return s;
}

/* -------------------------------------------------------------------------- */
/* Knot-along progress                                                        */
/* -------------------------------------------------------------------------- */

export interface Progress {
  /** Design id (a hash of the encoded design). */
  id: string;
  /** Number of steps completed. 0 means "not started". */
  done: number;
  updatedAt: number;
  name: string;
  totalSteps: number;
}

type ProgressMap = Record<string, Progress>;

function loadProgressMap(): ProgressMap {
  const raw = safeGet(KEY_PROGRESS);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ProgressMap) : {};
  } catch {
    return {};
  }
}

export function loadProgress(id: string): Progress | undefined {
  return loadProgressMap()[id];
}

export function saveProgress(p: Progress): void {
  const map = loadProgressMap();
  map[p.id] = p;
  // Keep the ten most recent, so a year of bracelets cannot fill her storage.
  const trimmed = Object.values(map)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 10);
  safeSet(KEY_PROGRESS, JSON.stringify(Object.fromEntries(trimmed.map((x) => [x.id, x]))));
}

export function clearProgress(id: string): void {
  const map = loadProgressMap();
  delete map[id];
  safeSet(KEY_PROGRESS, JSON.stringify(map));
}

export function allProgress(): Progress[] {
  return Object.values(loadProgressMap()).sort((a, b) => b.updatedAt - a.updatedAt);
}

/* -------------------------------------------------------------------------- */
/* Recently opened                                                            */
/* -------------------------------------------------------------------------- */

export interface RecentDesign {
  id: string;
  code: string;
  name: string;
  openedAt: number;
}

export function loadRecent(): RecentDesign[] {
  const raw = safeGet(KEY_RECENT);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentDesign[]) : [];
  } catch {
    return [];
  }
}

export function rememberDesign(entry: Omit<RecentDesign, 'openedAt'>): void {
  const list = loadRecent().filter((r) => r.id !== entry.id);
  list.unshift({ ...entry, openedAt: Date.now() });
  safeSet(KEY_RECENT, JSON.stringify(list.slice(0, 8)));
}

/**
 * A short, stable id for a design, derived from its encoded form.
 * Used as the progress key so the same shared link resumes in the same place.
 */
export function designId(encoded: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < encoded.length; i++) {
    h ^= encoded.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}
